import { lazy, Suspense, useContext } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

// Layout (always needed — keep eager)
import ScrollToTop from "./layouts/ScrollToTop";
import Header from "./layouts/MainNav.jsx";
import Setting from "./layouts/Setting";
import { ThemeContext } from "../context/ThemeContext";

// Lazy-loaded route components — only downloaded when the user navigates to them
const AdminDashboard = lazy(() => import("./components/placementDashboard/dashboardComponents/AdminDashboard.jsx"));
const Contacts = lazy(() => import("./components/contacts/Contacts.jsx"));
const Students = lazy(() => import("./components/students/Students.jsx"));
const Organizations = lazy(() => import("./components/organization/Organizations.jsx"));
const Placements = lazy(() => import("./components/placement/Placements.jsx"));
const AppliedJobs = lazy(() => import("./components/applied-jobs/AppliedJobs.jsx"));
const UserProfile = lazy(() => import("./components/UserProfile/UserProfile.jsx"));
const ListPlacementOfficers = lazy(() => import("./components/placementOfficers/ListPlacementOfficers.jsx"));
const AddPlacementOfficer = lazy(() => import("./components/placementOfficers/AddPlacementOfficer.jsx"));
const LockScreen = lazy(() => import("./pages/LockScreen"));
const Error404 = lazy(() => import("./pages/Error404"));
const Logout = lazy(() => import("./layouts/Logout"));
const Loading = lazy(() => import("./pages/Loading"));

// Settings pages
const AddOperation = lazy(() => import("./components/Settings/Opreations/AddOpreation.jsx"));
const ListOperation = lazy(() => import("./components/Settings/Opreations/ListOpreation.jsx"));
const AddApps = lazy(() => import("./components/setting/Apps/AddApp.jsx"));
const AppList = lazy(() => import("./components/setting/Apps/AppList.jsx"));
const AddAssignModules = lazy(() => import("./components/setting/assignmodule/AddAssignModules.jsx"));
const AssignModules = lazy(() => import("./components/setting/assignmodule/AssignModules.jsx"));
const AddRole = lazy(() => import("./components/setting/roles/AddRoles.jsx"));
const Role = lazy(() => import("./components/setting/roles/Roles.jsx"));
const AddApptoRole = lazy(() => import("./components/setting/ApptoRole/AddApptoRole.jsx"));
const ListApptoRole = lazy(() => import("./components/setting/ApptoRole/ListApptoRole.jsx"));
const AddUser = lazy(() => import("./components/setting/user/AddUser.jsx"));
const User = lazy(() => import("./components/setting/user/User.jsx"));
const OMList = lazy(() => import("./components/setting/OperationstoModules/OMList.jsx"));
const AddOMList = lazy(() => import("./components/setting/OperationstoModules/AddOM.jsx"));
const MTAppList = lazy(() => import("./components/setting/ModulesToApps/MTAppList.jsx"));
const AddMTApps = lazy(() => import("./components/setting/ModulesToApps/AddMTApps.jsx"));

const PageSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const allroutes = [
  { url: "placement-dashboard", component: <AdminDashboard /> },
  { url: "user-profile", component: <UserProfile /> },
  { url: "contacts", component: <Contacts /> },
  { url: "organizations", component: <Organizations /> },
  { url: "job-profiles", component: <Placements /> },
  { url: "placement-students", component: <Students /> },
  { url: "reports", component: <div style={{ marginTop: "200px" }}>Reports Component - To be implemented</div> },
  { url: "applied-jobs", component: <AppliedJobs /> },
  { url: "placement-officers", component: <ListPlacementOfficers /> },
  { url: "add-placement-officer", component: <AddPlacementOfficer /> },
  { url: "edit-placement-officer/:id", component: <AddPlacementOfficer /> },
  { url: "logout", component: <Logout /> },
  { url: "setting/Add-opreation", component: <AddOperation /> },
  { url: "setting/list-opreation", component: <ListOperation /> },
  { url: "setting/list-apps", component: <AppList /> },
  { url: "setting/add-app", component: <AddApps /> },
  { url: "setting/edit-app/:id", component: <AddApps /> },
  { url: "setting/list-modules", component: <AssignModules /> },
  { url: "setting/add-modules", component: <AddAssignModules /> },
  { url: "setting/edit-modules/:id", component: <AddAssignModules /> },
  { url: "setting/add-Roles", component: <AddRole /> },
  { url: "setting/list-Roles", component: <Role /> },
  { url: "setting/edit-Roles/:id", component: <AddRole /> },
  { url: "setting/operationstomodules", component: <OMList /> },
  { url: "setting/add-operation-to-modules", component: <AddOMList /> },
  { url: "setting/edit-operation-to-modules/:id", component: <AddOMList /> },
  { url: "setting/mta-list", component: <MTAppList /> },
  { url: "setting/add-module-to-app", component: <AddMTApps /> },
  { url: "setting/edit-module-to-app/:id", component: <AddMTApps /> },
  { url: "setting/add-app-to-role", component: <AddApptoRole /> },
  { url: "setting/list-app-to-role", component: <ListApptoRole /> },
  { url: "setting/edit-app-to-role/:id", component: <AddApptoRole /> },
  { url: "setting/adduser", component: <AddUser /> },
  { url: "setting/List-user", component: <User /> },
  { url: "setting/edituser/:id", component: <AddUser /> },
  { url: "/loading", component: <Loading /> },
];

const Markup = () => {
  return (
    <>
      <Routes>
        <Route path="/page-lock-screen" element={
          <Suspense fallback={<PageSpinner />}><LockScreen /></Suspense>
        } />

        {/* Redirect authenticated users from login pages */}
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/page-register" element={<Navigate to="/" replace />} />
        <Route path="/forgot-password" element={<Navigate to="/" replace />} />
        <Route path="/reset-password" element={<Navigate to="/" replace />} />
        
        {/* The root route (/) is handled by MainLayout and the auto-redirect logic in MainNav */}


        <Route element={<MainLayout />}>
          {allroutes.map((data, i) => (
            <Route
              key={i}
              exact
              path={data.url}
              element={<Suspense fallback={<PageSpinner />}>{data.component}</Suspense>}
            />
          ))}
          <Route path="*" element={<Suspense fallback={<PageSpinner />}><Error404 /></Suspense>} />
          <Route path="/page-error-404" element={<Suspense fallback={<PageSpinner />}><Error404 /></Suspense>} />
        </Route>
      </Routes>
      <ScrollToTop />
    </>
  );
};

function MainLayout() {
  const { sidebariconHover } = useContext(ThemeContext);
  const sideMenu = useSelector((state) => state.sideMenu);
  return (
    <div
      id="main-wrapper"
      className={`show ${sidebariconHover ? "iconhover-toggle" : ""} ${
        sideMenu ? "menu-toggle" : ""
      }`}
    >
      <Header />
      <div style={{ marginTop: "200px", padding: "0" }}>
        <div
          className="container-fluid"
          style={{ minHeight: window.screen.height - 45, padding: 0 }}
        >
          <Outlet />
        </div>
      </div>
      <Setting />
    </div>
  );
}

export default Markup;
